import { useEffect, useState } from "react";
import { Topbar } from "./components/Topbar";
import { ScenarioPanel } from "./components/ScenarioPanel";
import { BlockPanel } from "./components/BlockPanel";
import Map2D from "./components/Map2D";
import View3D from "./components/View3D";
import { TimeSliders } from "./components/TimeSliders";
import { ProgressBar } from "./components/ProgressBar";
import { HeatmapLegend } from "./components/HeatmapLegend";
import { BasemapToggle } from "./components/BasemapToggle";
import { useStore } from "./state/store";
import { startAutoSave } from "./state/autoSave";

export default function App() {
  // Initial bootstrap: laad publieke + persoonlijke lijsten, check auth-
  // state en server-instellingen, herstel het laatst-gebruikte scenario of
  // val terug op de admin-default. Start daarna auto-save.
  useEffect(() => {
    const state = useStore.getState();
    let cancelled = false;
    (async () => {
      await Promise.all([
        state.refreshPersonalList(),
        state.refreshPublicList(),
        state.refreshAuth(),
        state.refreshAppSettings(),
      ]);
      if (cancelled) return;
      await state.loadInitialScenario();
    })();
    const stop = startAutoSave();
    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  // Globale toetsenbord-bindings: Esc / Enter / Delete + Ctrl+Z/Y.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const state = useStore.getState();
      const target = e.target as HTMLElement | null;
      const inField =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable);

      if (e.key === "Escape") {
        if (state.drawMode !== "idle") {
          state.setDrawMode("idle");
        } else {
          state.selectBlock(null);
        }
        return;
      }

      if (inField) return;

      if (e.key === "Enter") {
        if (state.drawMode === "draw-polygon" && state.drawing.points.length >= 3) {
          state.finishPolygonDraw();
        }
        return;
      }

      if ((e.key === "Delete" || e.key === "Backspace") && state.selectedBlockId) {
        state.removeBlock(state.selectedBlockId);
        return;
      }

      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) state.redo();
        else state.undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        state.redo();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="h-screen flex flex-col">
      <Topbar />
      <div className="flex-1 flex min-h-0 relative">
        <ResponsivePanel side="left">
          <ScenarioPanel />
        </ResponsivePanel>
        {/* De middel-kolom (kaart + tijdsbalk) en het rechterpaneel zitten
            in één wrapper met `data-snapshot-area` zodat de PNG-export bij
            een geselecteerd blok het hele relevante gebied meeneemt. De
            tijdsbalk staat binnen de middel-kolom — niet onder de zijpanelen
            — zodat hij op smalle schermen niet wegvalt. */}
        <div className="flex flex-1 min-w-0" data-snapshot-area>
          <div className="flex flex-col flex-1 min-w-0 min-h-0">
            <main className="flex-1 relative min-w-0 min-h-0">
              <ViewSwitcher />
              <BasemapToggle />
              <HeatmapLegend />
              <ProgressBar />
            </main>
            <TimeSliders />
          </div>
          <ResponsivePanel side="right">
            <BlockPanel />
          </ResponsivePanel>
        </div>
      </div>
      <Footer />
    </div>
  );
}

// Op md+ tonen we beide zijpanelen naast de kaart. Onder md (tablet/mobiel)
// schuiven ze als overlay binnen het kaart-gebied. Belangrijk: de backdrop
// is `absolute inset-0` (niet `fixed`), zodat de tijdsbalk en footer eronder
// bedienbaar blijven. Anders zou tappen op een slider het paneel sluiten in
// plaats van de slider te bedienen.
function ResponsivePanel({
  side,
  children,
}: {
  side: "left" | "right";
  children: React.ReactNode;
}) {
  const open = useStore((s) =>
    side === "left" ? s.leftPanelOpen : s.rightPanelOpen,
  );
  const setOpen = useStore((s) =>
    side === "left" ? s.setLeftPanelOpen : s.setRightPanelOpen,
  );
  return (
    <>
      {open && (
        <div
          className="md:hidden absolute inset-0 z-30 bg-black/40"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        className={
          "z-40 md:relative md:z-auto md:translate-x-0 transition-transform " +
          (side === "left"
            ? "absolute left-0 top-0 bottom-0 "
            : "absolute right-0 top-0 bottom-0 ") +
          (open
            ? "translate-x-0"
            : side === "left"
              ? "-translate-x-full md:translate-x-0"
              : "translate-x-full md:translate-x-0") +
          (open ? "" : " md:flex")
        }
      >
        {children}
      </div>
    </>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground leading-snug space-y-1">
      <p>
        <strong>Indicatieve weergave</strong> van geometrische schaduw o.b.v.
        zonpositie. Geen rekening met bewolking, reflectie, atmosferische
        verstrooiing, schuine daken of bestaande beplanting (tenzij
        ingetekend). Voor juridische procedures: schakel een gecertificeerd
        bureau in.
      </p>
      <p>
        Luchtfoto © Beeldmateriaal Nederland / PDOK. Schaduwtool verzamelt
        geen persoonsgegevens behalve een admin-username.
      </p>
    </footer>
  );
}

// We tonen 2D en 3D als gestapelde lagen met een fade-transitie. Map2D
// blijft permanent gemount zodat de PDOK-tegels niet opnieuw geladen
// worden bij toggle. View3D wordt pas op de eerste 3D-activatie gemount
// (om de initiële tile-fetch en WebGL-context uit te stellen) en blijft
// daarna actief zodat bewerkingen aan blokken/zon real-time door werken.
function ViewSwitcher() {
  const viewMode = useStore((s) => s.viewMode);
  const [view3DEverActivated, setActivated] = useState(false);
  useEffect(() => {
    if (viewMode === "3d") setActivated(true);
  }, [viewMode]);

  return (
    <div className="absolute inset-0">
      <div
        className={
          "absolute inset-0 transition-opacity duration-300 " +
          (viewMode === "2d" ? "opacity-100" : "opacity-0 pointer-events-none")
        }
      >
        <Map2D />
      </div>
      <div
        className={
          "absolute inset-0 transition-opacity duration-300 " +
          (viewMode === "3d" ? "opacity-100" : "opacity-0 pointer-events-none")
        }
      >
        {view3DEverActivated && <View3D />}
      </div>
    </div>
  );
}
